import React, { useEffect, useState, useCallback } from 'react';
import { useFilter, useProductionData, useSort } from './hooks';
import { DashboardTable } from './DashboardTable';
import { EmployeeBarChart, StatusPieChart, TaskDonutChart, DailyLineChart } from './DashboardCharts';
import { styles, cssStyles } from './dashboardStyles';
import { ProductionRow, ProductionStats, SortConfig } from './types';
import agent from '../../app/api/agent';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Helper formatters
// const formatToDDMMYYYY = (date: Date | null): string => {
//   if (!date || isNaN(date.getTime())) return '';
//   const day = String(date.getDate()).padStart(2, '0');
//   const month = String(date.getMonth() + 1).padStart(2, '0');
//   const year = date.getFullYear();
//   return `${day}-${month}-${year}`;
// };

const formatToMMDDYYYY = (date: Date | null): string => {
  if (!date || isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`; // For API payload
};

// Custom hook for data fetching
const useProductionDataFetch = () => {
  const [data, setData] = useState<ProductionRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  const fetchData = useCallback(async (startDate: string, endDate: string, taskType: string, showLoading: boolean = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const payload = {
        StartDate: startDate,
        EndDate: endDate,
        department: taskType,
        ProjectId: "",
        EmpId: ""
      }
      const response = await agent.NFAIRestoTimeSheet.NFAIRestorationTimeSheet(payload);

      let extractedData: ProductionRow[] = [];
      if (Array.isArray(response)) {
        extractedData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        extractedData = response.data;
      }

      setData(extractedData);
      setLastFetch(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load production data';
      setError(errorMessage);
      console.error('Error fetching production data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchData, lastFetch };
};

// DateRangePicker Component (clean & controlled)
const DateRangePicker: React.FC<{
  startDate: Date;
  endDate: Date;
  onStartChange: (date: Date) => void;
  onEndChange: (date: Date) => void;
  onApply: (start: string, end: string) => void;
}> = ({ startDate, endDate, onStartChange, onEndChange, onApply }) => {
  const [localStart, setLocalStart] = useState<Date | null>(startDate);
  const [localEnd, setLocalEnd] = useState<Date | null>(endDate);

  // Sync local state when parent dates change (e.g. on refresh or initial load)
  useEffect(() => {
    setLocalStart(startDate);
    setLocalEnd(endDate);
  }, [startDate, endDate]);

  const handleApply = () => {
    if (localStart && localEnd) {
      const payloadStart = formatToMMDDYYYY(localStart);
      const payloadEnd = formatToMMDDYYYY(localEnd);

      // Update parent state
      onStartChange(localStart);
      onEndChange(localEnd);

      // Trigger API call with MM-DD-YYYY
      onApply(payloadStart, payloadEnd);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <label style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>From:</label>
        <DatePicker
          selected={localStart}
          onChange={(date: Date | null) => setLocalStart(date)}
          selectsStart
          startDate={localStart}
          endDate={localEnd}
          dateFormat="dd-MM-yyyy"
          maxDate={new Date()}
          customInput={
            <input
              style={{
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                width: '130px',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontFamily: 'inherit',
                color: '#1e293b',
              }}
            />
          }
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <label style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>To:</label>
        <DatePicker
          selected={localEnd}
          onChange={(date: Date | null) => setLocalEnd(date)}
          selectsEnd
          startDate={localStart}
          endDate={localEnd}
          minDate={localStart || undefined}
          maxDate={new Date()}
          dateFormat="dd-MM-yyyy"
          customInput={
            <input
              style={{
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                width: '130px',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontFamily: 'inherit',
                color: '#1e293b',
              }}
            />
          }
        />
      </div>

      <button
        onClick={handleApply}
        style={{
          padding: '8px 20px',
          backgroundColor: '#0ea5e9',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0284c7')}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#0ea5e9')}
      >
        <span>🔍</span>
        Apply
      </button>
    </div>
  );
};

// MetricCard Component (unchanged)
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  unit: string;
  subtitle: string;
  color: string;
  icon: string;
  trend?: string;
  trendPositive?: boolean;
}> = ({ title, value, unit, subtitle, color, icon, trend, trendPositive }) => (
  <div style={styles.metricCard} className="metric-card">
    <div style={styles.metricHeader}>
      <div style={{ ...styles.metricIcon, backgroundColor: `${color}15` }}>
        <span style={{ fontSize: '24px' }}>{icon}</span>
      </div>
      {trend && (
        <span
          style={{
            ...styles.trendBadge,
            color: trendPositive ? '#10b981' : '#ef4444',
            backgroundColor: trendPositive ? '#d1fae515' : '#fee2e215',
          }}
        >
          {trend}
        </span>
      )}
    </div>
    <div style={styles.metricBody}>
      <p style={styles.metricLabel}>{title}</p>
      <div style={styles.metricValueRow}>
        <span style={{ ...styles.metricValue, color }}>{value}</span>
        <span style={styles.metricUnit}>{unit}</span>
      </div>
      <p style={styles.metricSubtitle}>{subtitle}</p>
    </div>
  </div>
);

// Main Dashboard Component
const ProductionDashboard: React.FC = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // Store actual Date objects
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(firstDayOfMonth);
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(today);

  const taskType = 'Mastering'; // Change or make dynamic if needed

  const { data: rawData, loading, error, fetchData, lastFetch } = useProductionDataFetch();

  const stats: ProductionStats = useProductionData(rawData);
  const [filteredData, filter, setFilter] = useFilter(rawData);
  const [sortedData, , ] = useSort(filteredData) as [
    ProductionRow[],
    (key: keyof ProductionRow) => void,
    SortConfig
  ];

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Initial load on mount
  useEffect(() => {
    const startPayload = formatToMMDDYYYY(firstDayOfMonth);
    const endPayload = formatToMMDDYYYY(today);
    fetchData(startPayload, endPayload, taskType, true);
    //eslint-disable-next-line
  }, [fetchData]); // Only once

  const handleApply = (startPayload: string, endPayload: string) => {
    fetchData(startPayload, endPayload, taskType, true);
  };

  const handleRefresh = () => {
    const startPayload = formatToMMDDYYYY(selectedStartDate);
    const endPayload = formatToMMDDYYYY(selectedEndDate);
    fetchData(startPayload, endPayload, taskType, true);
  };

  // Loading state
  if (loading && rawData.length === 0) {
    return (
      <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #0ea5e9',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          ></div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <p style={{ fontSize: '18px', color: '#64748b', fontWeight: '500' }}>Loading production data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && rawData.length === 0) {
    return (
      <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '32px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ fontSize: '24px', marginBottom: '12px', color: '#1e293b', fontWeight: '700', marginTop: "40px" }}>Error Loading Data</h2>
          <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '24px' }}>{error}</p>
          <button
            onClick={handleRefresh}
            style={{
              padding: '12px 24px',
              backgroundColor: '#0ea5e9',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (rawData.length === 0) {
    return (
      <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>📭</div>
          <h2 style={{ fontSize: '24px', marginBottom: '8px', color: '#1e293b', fontWeight: '700' }}>No Production Data</h2>
          <p style={{ fontSize: '16px', color: '#64748b' }}>No data available for the selected date range</p>
          <button
            onClick={handleRefresh}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              backgroundColor: '#0ea5e9',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{cssStyles}</style>

      <header style={styles.header}>
        <div style={styles.headerTop}>
          <div>
            <h1 style={styles.title}>Production Overview</h1>
            <p style={styles.subtitle}>{currentDate}</p>
          </div>

          <DateRangePicker
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            onStartChange={setSelectedStartDate}
            onEndChange={setSelectedEndDate}
            onApply={handleApply}
          />

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={styles.headerBadge}>
              <span style={styles.liveDot}></span>
              <span style={styles.liveText}>Live Data</span>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              style={{
                padding: '8px 16px',
                backgroundColor: loading ? '#e2e8f0' : '#fff',
                color: loading ? '#94a3b8' : '#0ea5e9',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}>🔄</span>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            {lastFetch && (
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                Last updated: {lastFetch.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </header>

      {error && rawData.length > 0 && (
        <div
          style={{
            margin: '16px 20px',
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#991b1b',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>⚠️</span>
          <span>Warning: {error} (Showing cached data)</span>
        </div>
      )}

      <div style={styles.kpiSection}>
        <div style={styles.kpiGrid}>
          <MetricCard title="Total Production Hours" value={stats.totalHours} unit="hours" subtitle="Across all tasks" color="#0ea5e9" icon="⏱" />
          <MetricCard title="Active Projects" value={stats.uniqueTitles} unit="titles" subtitle="In production" color="#8b5cf6" icon="📽" />
          <MetricCard title="Completion Rate" value={stats.completionRate} unit="%" subtitle={`${stats.completed} of ${stats.totalTasks} tasks`} color="#10b981" icon="✓" />
          <MetricCard title="Reels Processed" value={stats.totalReels} unit="reels" subtitle="Total output" color="#f59e0b" icon="🎬" />
        </div>
      </div>

      <div style={styles.chartsSection}>
        <div style={styles.chartRow}>
          <div style={styles.chartCardLarge}>
            <div style={styles.chartHeader}>
              <h3 style={styles.chartTitle}>Employee Productivity</h3>
              <p style={styles.chartSubtitle}>Production hours by team member</p>
            </div>
            <div style={styles.chartContainer}>
              <EmployeeBarChart data={stats.employeeHours} />
            </div>
          </div>
          <div style={styles.chartCardMedium}>
            <div style={styles.chartHeader}>
              <h3 style={styles.chartTitle}>Task Status</h3>
              <p style={styles.chartSubtitle}>Current workflow distribution</p>
            </div>
            <div style={styles.chartContainer}>
              <StatusPieChart data={stats.statusCounts} />
            </div>
          </div>
          <div style={styles.chartCardMedium}>
            <div style={styles.chartHeader}>
              <h3 style={styles.chartTitle}>Task Categories</h3>
              <p style={styles.chartSubtitle}>Work breakdown by type</p>
            </div>
            <div style={styles.chartContainer}>
              <TaskDonutChart data={stats.taskTypes} />
            </div>
          </div>
          <div style={styles.chartCardLarge}>
            <div style={styles.chartHeader}>
              <h3 style={styles.chartTitle}>Daily Production Trend</h3>
              <p style={styles.chartSubtitle}>Hours tracked per day</p>
            </div>
            <div style={styles.chartContainer}>
              <DailyLineChart data={stats.dailyProduction} />
            </div>
          </div>
        </div>
      </div>

      <DashboardTable
        sortedData={sortedData}
        filter={filter}
        setFilter={setFilter}
        stats={stats}
        rawDataLength={rawData.length}
      />
    </div>
  );
};

export default ProductionDashboard;